import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ArrowLeft, Save, Camera, X } from 'lucide-react';
import { MainLayout } from '@/components/layout';
import { Button, Card } from '@/components/common';
import { toast } from 'react-toastify';
import { API_BASE_URL, API_ENDPOINTS } from '@/constants';
import { storageService } from '@/services';
import { cowApi } from '@/store/api/cowApi';

interface CowFormData {
  cowCode: string;
  name: string;
  sensorId: string;
  farmId: string;
  gender: 'MALE' | 'FEMALE';
  breed: string;
  cattleType: string;
  birthDate: string;
  color: string;
  weight: string;
  motherId: string;
  fatherId: string;
  healthStatus: 'HEALTHY' | 'SICK' | 'TREATMENT' | 'RECOVERED';
  purchaseDate: string;
  purchasePrice: string;
  currentValue: string;
  status: 'ACTIVE' | 'SOLD' | 'DEAD' | 'ARCHIVED';
  notes: string;
}

interface ImagePreview {
  file: File;
  preview: string;
}

interface Farm {
  id: string;
  name: string;
  farmCode: string;
}

export default function CowFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEditMode = !!id;

  const [farms, setFarms] = useState<Farm[]>([]);
  const [formData, setFormData] = useState<CowFormData>({
    cowCode: '',
    name: '',
    sensorId: '',
    farmId: '',
    gender: 'FEMALE',
    breed: 'BRAHMAN',
    cattleType: '',
    birthDate: '',
    color: '',
    weight: '',
    motherId: '',
    fatherId: '',
    healthStatus: 'HEALTHY',
    purchaseDate: '',
    purchasePrice: '',
    currentValue: '',
    status: 'ACTIVE',
    notes: '',
  });

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [selectedImages, setSelectedImages] = useState<ImagePreview[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchFarms();
    if (isEditMode) {
      fetchCowData();
    } else {
      setFetchLoading(false);
    }
  }, [id]);

  const fetchFarms = async () => {
    try {
      const token = storageService.getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.FARMS.BASE}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch farms');
      const data = await response.json();
      setFarms(data);

      // Set default farm if creating new cow
      if (!isEditMode && data.length > 0) {
        setFormData((prev) => ({ ...prev, farmId: data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching farms:', error);
      toast.error('ไม่สามารถโหลดรายการฟาร์มได้');
    }
  };

  const fetchCowData = async () => {
    try {
      const token = storageService.getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.COWS.DETAIL(id!)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch cow data');

      const data = await response.json();
      setFormData({
        cowCode: data.cowCode || '',
        name: data.name || '',
        sensorId: data.sensorId || '',
        farmId: data.farmId || '',
        gender: data.gender || 'FEMALE',
        breed: data.breed || 'BRAHMAN',
        cattleType: data.cattleType || '',
        birthDate: data.birthDate ? data.birthDate.split('T')[0] : '',
        color: data.color || '',
        weight: data.weight?.toString() || '',
        motherId: data.motherId || '',
        fatherId: data.fatherId || '',
        healthStatus: data.healthStatus || 'HEALTHY',
        purchaseDate: data.purchaseDate ? data.purchaseDate.split('T')[0] : '',
        purchasePrice: data.purchasePrice?.toString() || '',
        currentValue: data.currentValue?.toString() || '',
        status: data.status || 'ACTIVE',
        notes: data.notes || '',
      });
    } catch (error) {
      console.error('Error fetching cow:', error);
      toast.error('ไม่สามารถโหลดข้อมูลโคได้');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    // Filter valid files
    const validFiles = Array.from(files).filter((file) => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`ไฟล์ ${file.name} ไม่ใช่รูปภาพที่รองรับ`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`ไฟล์ ${file.name} มีขนาดใหญ่เกิน 5MB`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Read all files as data URLs using Promise.all
    try {
      const imagePromises = validFiles.map((file) => {
        return new Promise<ImagePreview>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              file,
              preview: reader.result as string,
            });
          };
          reader.onerror = () => {
            reject(new Error(`Failed to read ${file.name}`));
          };
          reader.readAsDataURL(file);
        });
      });

      const newImages = await Promise.all(imagePromises);
      setSelectedImages((prev) => [...prev, ...newImages]);
      toast.success(`เลือกรูปภาพสำเร็จ ${newImages.length} รูป`);
    } catch (error) {
      console.error('Error reading images:', error);
      toast.error('เกิดข้อผิดพลาดในการอ่านรูปภาพ');
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    toast.success('ลบรูปภาพสำเร็จ');
  };

  const uploadImages = async (cowId: string) => {
    const token = storageService.getAuthToken();

    for (let i = 0; i < selectedImages.length; i++) {
      try {
        const response = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.COWS.ADD_IMAGE(cowId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              url: selectedImages[i].preview, // base64 data URL
              isPrimary: i === 0, // First image is primary
            }),
          }
        );

        if (!response.ok) {
          console.error('Failed to upload image:', i);
        }
      } catch (error) {
        console.error('Error uploading image:', error);
      }
    }

    // Invalidate RTK Query cache to refresh cow data with new images
    dispatch(cowApi.util.invalidateTags([
      { type: 'Cow', id: cowId },
      { type: 'Cow', id: 'LIST' }
    ]));
  };

  const parseErrorMessage = (error: any): string => {
    // ถ้ามี error.message ให้ใช้
    const message = error.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล';

    // แปลง error message ให้อ่านง่ายขึ้น
    if (message.includes('Sensor ID') && message.includes('มีในระบบแล้ว')) {
      const sensorId = message.match(/"([^"]+)"/)?.[1] || 'ที่ระบุ';
      return `❌ ไม่สามารถบันทึกได้\n\nเซ็นเซอร์ ID: ${sensorId} มีในระบบแล้ว\nกรุณาตรวจสอบและใช้เซ็นเซอร์ ID อื่น`;
    }

    if (message.includes('Cow code') && message.includes('already exists')) {
      return '❌ รหัสโคนี้มีในระบบแล้ว กรุณาใช้รหัสอื่น';
    }

    if (message.includes('Farm not found')) {
      return '❌ ไม่พบฟาร์มที่เลือก กรุณาเลือกฟาร์มใหม่';
    }

    if (message.includes('duplicate') || message.includes('already exists')) {
      return '❌ ข้อมูลซ้ำกับในระบบ กรุณาตรวจสอบข้อมูล';
    }

    if (message.includes('required')) {
      return '❌ กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน';
    }

    if (message.includes('invalid')) {
      return '❌ ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบข้อมูลอีกครั้ง';
    }

    // กรณีอื่นๆ ให้แสดง message เดิมพร้อมไอคอน error
    return `❌ ${message}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = storageService.getAuthToken();
      const url = isEditMode
        ? `${API_BASE_URL}${API_ENDPOINTS.COWS.UPDATE(id!)}`
        : `${API_BASE_URL}${API_ENDPOINTS.COWS.CREATE}`;

      const method = isEditMode ? 'PUT' : 'POST';

      const payload = {
        ...formData,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : null,
        currentValue: formData.currentValue ? parseFloat(formData.currentValue) : null,
        birthDate: formData.birthDate ? new Date(formData.birthDate).toISOString() : new Date().toISOString(),
        purchaseDate: formData.purchaseDate ? new Date(formData.purchaseDate).toISOString() : null,
        sensorId: formData.sensorId || null,
        cattleType: formData.cattleType || null,
        motherId: formData.motherId || null,
        fatherId: formData.fatherId || null,
        color: formData.color || null,
        notes: formData.notes || null,
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save cow');
      }

      const result = await response.json();

      // Upload images if any selected
      if (selectedImages.length > 0) {
        await uploadImages(result.id);
        toast.success(
          `${isEditMode ? 'แก้ไขโคสำเร็จ' : 'เพิ่มโคสำเร็จ'} และอัปโหลด ${selectedImages.length} รูปภาพ`,
          { autoClose: 3000 }
        );
      } else {
        toast.success(
          isEditMode ? '✅ แก้ไขข้อมูลโคสำเร็จ' : '✅ เพิ่มโคใหม่สำเร็จ',
          { autoClose: 2000 }
        );
      }

      navigate(`/cows/${result.id}`);
    } catch (error: any) {
      console.error('Error saving cow:', error);
      const errorMessage = parseErrorMessage(error);
      toast.error(errorMessage, {
        autoClose: 5000,
        style: {
          whiteSpace: 'pre-line',
          fontSize: '14px',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800"></div>
        </div>
      </MainLayout>
    );
  }

  const breeds = [
    { value: 'BRAHMAN', label: 'พันธุ์พราหมัน' },
    { value: 'CHAROLAIS', label: 'พันธุ์ชาร์โลเลส์' },
    { value: 'ANGUS', label: 'พันธุ์แองกัส' },
    { value: 'SIMMENTAL', label: 'พันธุ์ซิมเมนทอล' },
    { value: 'MIXED', label: 'พันธุ์ผสม' },
    { value: 'OTHER', label: 'อื่นๆ' },
  ];

  const cattleTypes = [
    { value: 'BREEDING_BULL', label: 'พ่อพันธุ์' },
    { value: 'BREEDING_COW', label: 'แม่พันธุ์' },
    { value: 'CALF', label: 'ลูกโค' },
    { value: 'YOUNG_CATTLE', label: 'โครุ่น' },
    { value: 'FATTENING_CATTLE', label: 'โคขุน' },
  ];

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            onClick={() => navigate(isEditMode ? `/cows/${id}` : '/cows')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            กลับ
          </Button>
          <h1 className="text-3xl font-bold text-text-primary">
            {isEditMode ? 'แก้ไขข้อมูลโค' : 'เพิ่มโคใหม่'}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              ข้อมูลพื้นฐาน
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* รหัสโค */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  รหัสโค <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="cowCode"
                  value={formData.cowCode}
                  onChange={handleChange}
                  required
                  disabled={isEditMode}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder="COW001"
                />
              </div>

              {/* ชื่อ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ชื่อ (ถ้ามี)
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="แดง"
                />
              </div>

              {/* Sensor ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sensor ID
                </label>
                <input
                  type="text"
                  name="sensorId"
                  value={formData.sensorId}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="08A6F74AFE76"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ระบุเพื่อเชื่อมโยงกับระบบ sensor
                </p>
              </div>

              {/* ฟาร์ม */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ฟาร์ม <span className="text-red-500">*</span>
                </label>
                <select
                  name="farmId"
                  value={formData.farmId}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">เลือกฟาร์ม</option>
                  {farms.map((farm) => (
                    <option key={farm.id} value={farm.id}>
                      {farm.name} ({farm.farmCode})
                    </option>
                  ))}
                </select>
              </div>

              {/* เพศ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  เพศ <span className="text-red-500">*</span>
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="FEMALE">เมีย</option>
                  <option value="MALE">ผู้</option>
                </select>
              </div>

              {/* สายพันธุ์ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  สายพันธุ์ <span className="text-red-500">*</span>
                </label>
                <select
                  name="breed"
                  value={formData.breed}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {breeds.map((breed) => (
                    <option key={breed.value} value={breed.value}>
                      {breed.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* กลุ่มวัว */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  กลุ่มวัว
                </label>
                <select
                  name="cattleType"
                  value={formData.cattleType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">เลือกกลุ่มวัว</option>
                  {cattleTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* วันเกิด */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  วันเกิด <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* สี */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  สี
                </label>
                <input
                  type="text"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="น้ำตาล, ขาว, ดำ"
                />
              </div>

              {/* น้ำหนัก */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  น้ำหนัก (กก.)
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="weight"
                  value={formData.weight}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="350.5"
                />
              </div>

              {/* สถานะสุขภาพ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  สถานะสุขภาพ
                </label>
                <select
                  name="healthStatus"
                  value={formData.healthStatus}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="HEALTHY">สุขภาพดี</option>
                  <option value="SICK">ป่วย</option>
                  <option value="TREATMENT">กำลังรักษา</option>
                  <option value="RECOVERED">หายแล้ว</option>
                </select>
              </div>

              {/* สถานะ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  สถานะ
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="ACTIVE">ใช้งาน</option>
                  <option value="SOLD">ขายแล้ว</option>
                  <option value="DEAD">ตายแล้ว</option>
                  <option value="ARCHIVED">เก็บถาวร</option>
                </select>
              </div>

              {/* วันที่ซื้อ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  วันที่ซื้อ
                </label>
                <input
                  type="date"
                  name="purchaseDate"
                  value={formData.purchaseDate}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* ราคาซื้อ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ราคาซื้อ (บาท)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="purchasePrice"
                  value={formData.purchasePrice}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="15000"
                />
              </div>

              {/* มูลค่าปัจจุบัน */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  มูลค่าปัจจุบัน (บาท)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="currentValue"
                  value={formData.currentValue}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="20000"
                />
              </div>

              {/* หมายเหตุ */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  หมายเหตุ
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="บันทึกเพิ่มเติม..."
                />
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                รูปภาพวัว
              </h3>

              {/* Upload Button */}
              <div className="mb-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2"
                >
                  <Camera className="w-5 h-5" />
                  เลือกรูปภาพ
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  รองรับไฟล์: JPG, PNG, GIF (ขนาดไม่เกิน 5MB ต่อรูป)
                </p>
              </div>

              {/* Image Previews */}
              {selectedImages.length > 0 && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {selectedImages.map((image, index) => (
                    <div
                      key={index}
                      className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-300"
                    >
                      <img
                        src={image.preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {/* Primary Badge */}
                      {index === 0 && (
                        <div className="absolute top-2 left-2 bg-primary-800 text-white text-xs px-2 py-1 rounded">
                          รูปหลัก
                        </div>
                      )}
                      {/* Remove Button */}
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute top-2 right-2 bg-error text-white p-1 rounded-full hover:bg-red-700 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center gap-4 mt-6 pt-6 border-t">
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="flex items-center gap-2"
              >
                <Save className="w-5 h-5" />
                {loading ? 'กำลังบันทึก...' : 'บันทึก'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(isEditMode ? `/cows/${id}` : '/cows')}
                disabled={loading}
              >
                ยกเลิก
              </Button>
            </div>
          </Card>
        </form>
      </div>
    </MainLayout>
  );
}
